import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { ArrowDown, ArrowRight, Crosshair, ShieldCheck, UsersThree } from "@phosphor-icons/react";
import gsap from "gsap";
import { flushSync } from "react-dom";
import { AssessmentCategoryRules } from "../components/AssessmentCategoryRules";
import { BrandLogo } from "../components/BrandLogo";
import { AuthEntryButton } from "../components/AuthEntryDialog";
import { ExaminerLiveBoard } from "../components/ExaminerLiveBoard";
import { PublicSiteHeader } from "../components/PublicSiteHeader";
import { canUseWebGL, SignalFieldCanvas } from "../components/SignalFieldCanvas";
import { standards, specialRules } from "../data/standards";
import {
  normalizeStageForMode,
  sameIntroStage,
  stageFromHash,
  stepIntroStage,
  transitionDuration,
  type IntroDirection,
  type IntroStage
} from "../lib/introDeckMachine";
import { parseAssessmentCategory, rememberAssessmentCategory } from "../lib/assessmentSelection";
import { useMedia } from "../lib/useMedia";
import { usePublicExaminers } from "../lib/usePublicExaminers";
import { modeLabels, rankLabels, type AssessmentCategory, type ExamMode, type Rank } from "../shared/contracts";

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest("a, button, input, textarea, select, dialog, [role='dialog'], [data-intro-input-exempt]"));
}

function introControllerPaused() {
  return Boolean(document.querySelector("dialog[open], .site-header nav.is-open"));
}

function updateIntroHash(stage: IntroStage) {
  const nextHash = stage.kind === "HERO" ? "#hero" : "#ranks";
  if (window.location.hash !== nextHash) window.history.replaceState(null, "", nextHash);
}

function IntroDeck() {
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const mobile = useMedia("(max-width: 760px)");
  const deck = useRef<HTMLDivElement>(null);
  const screenMask = useRef<HTMLDivElement>(null);
  const rankMask = useRef<HTMLDivElement>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const animationLocked = useRef(false);
  const modeRef = useRef<ExamMode>("DOUBLE");
  const initialStage = useMemo<IntroStage>(() => stageFromHash(window.location.hash, "DOUBLE") || { kind: "HERO" }, []);
  const stageRef = useRef<IntroStage>(initialStage);
  const [stage, setStage] = useState<IntroStage>(initialStage);
  const [mode, setMode] = useState<ExamMode>("DOUBLE");
  const available = standards[mode];
  const activeRank = stage.kind === "RANK" ? stage.rank : available[0].rank;
  const activeIndex = Math.max(0, available.findIndex((item) => item.rank === activeRank));
  const active = available[activeIndex] || available[0];
  const webgl = useMemo(() => canUseWebGL(), []);

  const commitStage = useCallback((next: IntroStage) => {
    stageRef.current = next;
    setStage(next);
    updateIntroHash(next);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: deck.current?.offsetTop ?? 0, behavior: "auto" });
      window.requestAnimationFrame(() => window.scrollTo({ top: deck.current?.offsetTop ?? 0, behavior: "auto" }));
    });
  }, []);

  const transitionTo = useCallback((next: IntroStage) => {
    const previous = stageRef.current;
    if (sameIntroStage(previous, next) || animationLocked.current) return;
    const duration = reduced ? 0 : transitionDuration(previous, next);
    if (!duration) {
      commitStage(next);
      return;
    }

    const mask = duration === 420 ? screenMask.current : rankMask.current;
    const halves = mask ? Array.from(mask.querySelectorAll<HTMLElement>("[data-mask-half]")) : [];
    if (!mask || halves.length !== 2) {
      commitStage(next);
      return;
    }

    animationLocked.current = true;
    timeline.current?.kill();
    gsap.set(mask, { autoAlpha: 1 });
    gsap.set(halves, { scaleX: 0 });
    const halfDuration = duration / 2000;
    timeline.current = gsap.timeline({
      onComplete: () => {
        gsap.set(mask, { autoAlpha: 0 });
        animationLocked.current = false;
      }
    });
    timeline.current
      .to(halves, { scaleX: 1, duration: halfDuration, ease: "power3.in" }, 0)
      .call(() => flushSync(() => commitStage(next)))
      .to(halves, { scaleX: 0, duration: halfDuration, ease: "power3.out" });
  }, [commitStage, reduced]);

  const handleDirection = useCallback((direction: IntroDirection) => {
    if (animationLocked.current) return true;
    const outcome = stepIntroStage(stageRef.current, modeRef.current, direction);
    if (outcome.boundary !== "NONE") return false;
    transitionTo(outcome.stage);
    return true;
  }, [transitionTo]);

  useLayoutEffect(() => {
    if (reduced || !deck.current) return;
    const context = gsap.context(() => {
      if (stage.kind === "HERO") {
        const heroTimeline = gsap.timeline({ defaults: { overwrite: "auto" } });
        heroTimeline
          .fromTo(
            "[data-hero-beam]",
            { autoAlpha: 0, scaleY: 0.04, filter: "brightness(3)" },
            { autoAlpha: 1, scaleY: 1, filter: "brightness(1.5)", duration: 0.34, ease: "expo.out" },
            0.03
          )
          .fromTo(
            "[data-hero-signal]",
            { clipPath: mobile ? "inset(0 49.85% 0 49.85%)" : "inset(0 28% 0 72%)", scale: 1.075, filter: "brightness(2.4) contrast(1.25)" },
            { clipPath: "inset(0 0% 0 0%)", scale: 1, filter: "brightness(1) contrast(1)", duration: 1.16, ease: "expo.inOut" },
            0
          )
          .fromTo(
            ".hero-index, .hero-signal-status",
            { autoAlpha: 0, x: -22 },
            { autoAlpha: 1, x: 0, duration: 0.42, stagger: 0.08, ease: "power3.out" },
            0.24
          )
          .fromTo(
            ".hero-kicker",
            { autoAlpha: 0, x: -72, letterSpacing: ".32em" },
            { autoAlpha: 1, x: 0, letterSpacing: ".12em", duration: 0.58, ease: "expo.out" },
            0.42
          )
          .fromTo(
            ".hero-content h1 span",
            { autoAlpha: 0, x: (index) => index ? 130 : -105, y: 54, rotation: (index) => index ? 2.8 : -2.4, scale: 1.08, filter: "blur(7px)" },
            { autoAlpha: 1, x: 0, y: 0, rotation: 0, scale: 1, filter: "blur(0px)", duration: 0.72, stagger: 0.075, ease: "expo.out" },
            0.53
          )
          .fromTo(
            ".hero-lede",
            { autoAlpha: 0, y: 30 },
            { autoAlpha: 1, y: 0, duration: 0.46, ease: "power3.out" },
            0.82
          )
          .fromTo(
            ".hero-actions",
            { autoAlpha: 0, y: 38, scaleX: 0.82, transformOrigin: "left center" },
            { autoAlpha: 1, y: 0, scaleX: 1, duration: 0.54, ease: "back.out(1.7)" },
            0.92
          )
          .to("[data-hero-beam]", { autoAlpha: 0, duration: 0.28, ease: "power2.out" }, 1.12);
        return;
      }

      gsap.fromTo(
        "[data-rank-reveal]",
        { y: 24, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.56, stagger: 0.052, ease: "power3.out", overwrite: "auto" }
      );
    }, deck);
    return () => context.revert();
  }, [mobile, reduced, stage]);

  useEffect(() => {
    const node = deck.current;
    if (!node) return;
    const wheelGesture = { total: 0, consumed: false, direction: "DOWN" as IntroDirection, timer: 0 };
    const touchGesture = { startX: 0, startY: 0, active: false, triggered: false };
    const atDeckBoundary = () => window.scrollY <= node.offsetTop + 2;
    const resetWheel = () => {
      wheelGesture.total = 0;
      wheelGesture.consumed = false;
    };
    const scheduleWheelReset = () => {
      window.clearTimeout(wheelGesture.timer);
      wheelGesture.timer = window.setTimeout(resetWheel, 180);
    };

    const onWheel = (event: WheelEvent) => {
      if (!atDeckBoundary() || introControllerPaused() || event.ctrlKey || isInteractiveTarget(event.target) || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const direction: IntroDirection = event.deltaY >= 0 ? "DOWN" : "UP";
      const outcome = stepIntroStage(stageRef.current, modeRef.current, direction);
      scheduleWheelReset();
      if (wheelGesture.consumed || animationLocked.current) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      if (outcome.boundary !== "NONE") return;
      if (event.cancelable) event.preventDefault();
      if (wheelGesture.direction !== direction) {
        wheelGesture.direction = direction;
        wheelGesture.total = 0;
      }
      wheelGesture.total += Math.abs(event.deltaY);
      if (wheelGesture.total < 48) return;
      wheelGesture.consumed = true;
      handleDirection(direction);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (!atDeckBoundary() || introControllerPaused() || event.touches.length !== 1 || isInteractiveTarget(event.target)) return;
      touchGesture.startX = event.touches[0].clientX;
      touchGesture.startY = event.touches[0].clientY;
      touchGesture.active = true;
      touchGesture.triggered = false;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!touchGesture.active || event.touches.length !== 1) return;
      const dx = touchGesture.startX - event.touches[0].clientX;
      const dy = touchGesture.startY - event.touches[0].clientY;
      if (Math.abs(dy) < 10 || Math.abs(dy) <= Math.abs(dx) * 1.12) return;
      const direction: IntroDirection = dy > 0 ? "DOWN" : "UP";
      const outcome = stepIntroStage(stageRef.current, modeRef.current, direction);
      if (touchGesture.triggered || animationLocked.current || outcome.boundary === "NONE") {
        if (event.cancelable) event.preventDefault();
      }
      if (outcome.boundary !== "NONE" || touchGesture.triggered || animationLocked.current || Math.abs(dy) < 48) return;
      touchGesture.triggered = true;
      handleDirection(direction);
    };

    const onTouchEnd = () => {
      touchGesture.active = false;
      touchGesture.triggered = false;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!atDeckBoundary() || introControllerPaused() || event.repeat || event.metaKey || event.ctrlKey || event.altKey || isInteractiveTarget(event.target)) return;
      const direction: IntroDirection | null = event.key === "ArrowDown" || event.key === "PageDown" || event.code === "Space"
        ? "DOWN"
        : event.key === "ArrowUp" || event.key === "PageUp"
          ? "UP"
          : null;
      if (!direction) return;
      const outcome = stepIntroStage(stageRef.current, modeRef.current, direction);
      if (animationLocked.current || outcome.boundary === "NONE") {
        event.preventDefault();
        if (!animationLocked.current) handleDirection(direction);
      }
    };

    const onHashChange = () => {
      const next = stageFromHash(window.location.hash, modeRef.current);
      if (!next) return;
      timeline.current?.kill();
      animationLocked.current = false;
      stageRef.current = next;
      setStage(next);
      window.scrollTo({ top: node.offsetTop, behavior: "auto" });
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      window.clearTimeout(wheelGesture.timer);
      timeline.current?.kill();
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [handleDirection]);

  const selectMode = (nextMode: ExamMode) => {
    if (mode === nextMode || animationLocked.current) return;
    modeRef.current = nextMode;
    setMode(nextMode);
    const normalized = normalizeStageForMode(stageRef.current, nextMode);
    if (!sameIntroStage(normalized, stageRef.current)) transitionTo(normalized);
  };

  const selectRank = (rank: Rank) => transitionTo({ kind: "RANK", rank });
  const heroActive = stage.kind === "HERO";
  const rankActive = stage.kind === "RANK";

  return (
    <div className={`intro-deck ${rankActive ? "is-rank" : "is-hero"}`} ref={deck} style={{ "--rank-tone": active.tone } as CSSProperties}>
      <div className="intro-signal-field" data-hero-signal aria-hidden="true">
        {!reduced && webgl ? <SignalFieldCanvas mobile={mobile} subdued={rankActive} /> : <img src="/assets/yeyang-mark.png" alt="" />}
      </div>
      <div className="intro-ignition-beam" data-hero-beam aria-hidden="true" />
      <div className="intro-grid" aria-hidden="true" />
      <section className={`intro-panel hero-section ${heroActive ? "is-active" : ""}`} id="hero" aria-hidden={!heroActive} inert={!heroActive}>
        <div className="hero-index" aria-hidden="true"><span>LIVE</span><b>01</b><i /><b>04</b></div>
        <div className="hero-content">
          <div className="hero-kicker" data-hero-reveal><span>LIVE</span>野羊电竞 · 三角洲行动考核招募</div>
          <h1 data-hero-reveal><span>实力，</span><span>不需要解释</span></h1>
          <p className="hero-lede" data-hero-reveal>用一场公开考核，证明你的段位与价值。所有类别共享每周一次免费机会，另可使用补偿机会；段位考核支持付费资格。</p>
          <div className="hero-actions" data-hero-reveal>
            <AuthEntryButton />
            <a className="text-link" href="/candidate-guide#exam-standard">查看考核须知<ArrowRight size={18} /></a>
          </div>
          <div className="hero-category-links" aria-label="女陪考核入口">
            <a href="/candidate-guide?assessmentCategory=FEMALE_ENTERTAINMENT#exam-standard">女娱乐考核<ArrowRight size={16} /></a>
            <a href="/candidate-guide?assessmentCategory=FEMALE_TECHNICAL#exam-standard">女技术考核<ArrowRight size={16} /></a>
          </div>
        </div>
        <div className="hero-signal-status" aria-hidden="true"><span>SIGNAL STRENGTH</span><i /><small>STANDBY</small></div>
        <a className="scroll-cue" href="#ranks"><span>SCROLL TO PROVE</span><ArrowDown size={18} /></a>
      </section>
      <section className={`intro-panel rank-stage ${rankActive ? "is-active" : ""}`} id="ranks" aria-hidden={!rankActive} inert={!rankActive}>
        <div className="rank-shell">
          <div className="rank-heading" data-rank-reveal>
            <span className="eyebrow">02 / RANK SYSTEM</span>
            <h2>选择你要证明的等级</h2>
            <p>每次滑动只推进一个段位，真实战绩决定最终结果。</p>
          </div>
          <div className="mode-switch" aria-label="考核模式" data-rank-reveal>
            {(["DOUBLE", "SINGLE"] as ExamMode[]).map((item) => <button type="button" className={mode === item ? "is-active" : ""} onClick={() => selectMode(item)} key={item}>{modeLabels[item]}</button>)}
          </div>
          <div className="rank-tabs" role="tablist" aria-label="段位" data-rank-reveal>
            {available.map((item, index) => (
              <button type="button" role="tab" aria-selected={activeRank === item.rank} className={activeRank === item.rank ? "is-active" : ""} onClick={() => selectRank(item.rank)} key={item.rank}>
                <small>0{index + 1}</small><span>{rankLabels[item.rank]}</span>
              </button>
            ))}
          </div>
          <div className="rank-showcase">
            <div className="rank-copy" data-rank-reveal>
              <span className="rank-code">RANK / 0{activeIndex + 1}</span>
              <strong>{rankLabels[active.rank]}</strong>
              <p>{active.description}</p>
              <small>{specialRules[mode]}</small>
            </div>
            <dl className="rank-metrics" data-rank-reveal>
              <div><dt>有效局数</dt><dd>{active.rounds}</dd></div>
              <div><dt>目标击杀</dt><dd>{active.kills}<small> 杀</small></dd></div>
              <div><dt>撤离物资</dt><dd>{active.extractedValue}</dd></div>
            </dl>
            <div className="rank-change-mask" ref={rankMask} aria-hidden="true"><span data-mask-half /><span data-mask-half /></div>
          </div>
          <div className="rank-progress" aria-hidden="true"><span style={{ width: `${((activeIndex + 1) / available.length) * 100}%` }} /></div>
        </div>
      </section>
      <div className="intro-transition-mask" ref={screenMask} aria-hidden="true"><span data-mask-half /><span data-mask-half /><i /></div>
      <div className="sr-only" aria-live="polite">{heroActive ? "首页主视觉" : `${modeLabels[mode]} ${rankLabels[active.rank]}考核标准`}</div>
    </div>
  );
}

function ExaminerSection() {
  const { online, offline, loading, error } = usePublicExaminers();
  return (
    <section className="examiner-section full-screen-section" id="examiners">
      <div className="section-heading section-heading--row">
        <div><span className="eyebrow">03 / EXAMINERS</span><h2>现在，谁在开考</h2></div>
        <div className="examiner-section__actions">
          <div className="live-counter"><i />{online.length} 位考官直播中</div>
          <a href="/live-examiners">查看完整直播页<ArrowRight size={17} /></a>
        </div>
      </div>
      <ExaminerLiveBoard online={online} offline={offline} loading={loading} error={error} />
      <div className="examiner-note"><ShieldCheck size={22} /><span>考核与直播状态完全独立。官网只展示考官当前主动上线的直播记录。</span></div>
    </section>
  );
}

function StandardsSection() {
  const [assessmentCategory, setAssessmentCategory] = useState<AssessmentCategory>(() => typeof window === "undefined" ? "STANDARD" : parseAssessmentCategory(new URLSearchParams(window.location.search).get("assessmentCategory")) || "STANDARD");
  return (
    <section className="standards-section full-screen-section" id="standards">
      <div className="section-heading section-heading--row">
        <div><span className="eyebrow">04 / EXAM STANDARD</span><h2>最终考核标准</h2></div>
        <AuthEntryButton className="outline-button" children="申请本周考核" assessmentCategory={assessmentCategory} />
      </div>
      <div className="assessment-category-tabs" role="group" aria-label="考核类别">{(["STANDARD", "FEMALE_ENTERTAINMENT", "FEMALE_TECHNICAL"] as AssessmentCategory[]).map((category, index) => <button type="button" aria-pressed={assessmentCategory === category} className={assessmentCategory === category ? "is-active" : ""} onClick={() => { setAssessmentCategory(category); rememberAssessmentCategory(category); }} key={category}>{["段位考核", "女娱乐", "女技术"][index]}</button>)}</div>
      <AssessmentCategoryRules assessmentCategory={assessmentCategory} showEntry />
      {assessmentCategory === "STANDARD" && <>
      <div className="standards-table-wrap">
        <table className="standards-table">
          <thead><tr><th>考核模式</th><th>段位</th><th>等级局数</th><th>击杀</th><th>撤离物资</th></tr></thead>
          <tbody>
            {standards.DOUBLE.map((item, index) => <tr key={`double-${item.rank}`}><td>{index === 0 && <b>双考</b>}</td><td>{rankLabels[item.rank]}</td><td>{item.rounds}</td><td>{item.kills} 杀</td><td>{item.extractedValue}</td></tr>)}
            {standards.SINGLE.map((item, index) => <tr key={`single-${item.rank}`}><td>{index === 0 && <b>单考</b>}</td><td>{rankLabels[item.rank]}</td><td>{item.rounds}</td><td>{item.kills} 杀</td><td>{item.extractedValue}</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="rule-lines">
        <div><Crosshair size={20} /><span><b>双考特殊规则</b>{specialRules.DOUBLE}</span></div>
        <div><UsersThree size={20} /><span><b>单考特殊规则</b>{specialRules.SINGLE}</span></div>
      </div>
      </>}
      <footer className="site-footer"><BrandLogo compact /><span>© 2026 野羊电竞 · 实力不需要解释</span><a href="/admin/login">管理后台</a></footer>
    </section>
  );
}

export function HomePage() {
  useEffect(() => {
    const targetId = window.location.hash === "#examiners" || window.location.hash === "#standards" ? window.location.hash : null;
    if (!targetId) return;
    const frame = window.requestAnimationFrame(() => document.querySelector(targetId)?.scrollIntoView({ behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return <div className="home-page"><PublicSiteHeader active="home" /><IntroDeck /><ExaminerSection /><StandardsSection /></div>;
}
